import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { AuditLogService } from '../../Modules/Ops/Services/audit-log.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE, MAX_PAGE_SIZE } from '../../Consts/backoffice.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { AuditLog } from '../../Models/backoffice/audit-log.model';
import { OrganizationSummary } from '../../Models/backoffice/organization-summary.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationListResponse } from '../../Interfaces/api/backoffice/organization.interface';
import {
  AuditLogFilters,
  AuditLogListResponse,
} from '../../Interfaces/api/backoffice/audit-log.interface';

@Injectable()
export class AuditLogFacade {
  private readonly _auditLogService = inject(AuditLogService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _selectedEntryId = signal<string>('');

  private readonly _filters = signal<AuditLogFilters>({
    action: '',
    entity_type: '',
    organization_id: '',
    from: '',
    to: '',
  });

  private readonly _organizations = httpResource<LaravelDefaultResponse<OrganizationListResponse>>(
    () => this._organizationService.listRequest({}, FIRST_PAGE, MAX_PAGE_SIZE),
  );

  private readonly _entries = httpResource<LaravelDefaultResponse<AuditLogListResponse>>(() =>
    this._auditLogService.listRequest(this._filters(), this._page(), this._pageSize()),
  );

  readonly organizations = computed<OrganizationSummary[]>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizations)).items,
  );

  readonly filters = computed<AuditLogFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly entries = computed<AuditLog[]>(() =>
    this._auditLogService.toEntries(this._resourceState.valueOf(this._entries)),
  );

  readonly total = computed<number>(() => this._auditLogService.toTotal(this._resourceState.valueOf(this._entries)));

  readonly actions = computed<string[]>(() =>
    this._auditLogService.toActions(this._resourceState.valueOf(this._entries)),
  );

  readonly entityTypes = computed<string[]>(() =>
    this._auditLogService.toEntityTypes(this._resourceState.valueOf(this._entries)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._entries.status()),
  );

  readonly selectedEntryId = computed<string>(() => this._selectedEntryId());

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  applyFilters(filters: AuditLogFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({
      action: '',
      entity_type: '',
      organization_id: '',
      from: '',
      to: '',
    });
  }

  toggleEntry(entryId: string): void {
    this._selectedEntryId.set(this._selectedEntryId() === entryId ? '' : entryId);
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._entries.reload();
  }
}
